'use client';
import type { ProjectIconProps } from 'elowen-plugin-ui-kit';
import { useQuery } from '@tanstack/react-query';
import { FolderGit2, Laptop } from 'lucide-react';
import { elowenClient } from '../../lib/elowenClient';
import { usePluginPresent } from '../../lib/queries';

/** Single source of truth for rendering a project's identity glyph. When the project has an `icon`
 *  (project-relative, or guest-absolute for a managed system icon), it renders that image; otherwise the default
 *  folder glyph. The image bytes are fetched once per (project, icon) and cached as a data URL — shared
 *  across every pill/card on screen, so N project pills never trigger N fetches. Used everywhere a
 *  project is shown (filter pills, the Projects grid) so the icon stays consistent. */
export function ProjectIcon({ project, size = 16, className = '' }: ProjectIconProps) {
  if (project.executionKind === 'local' || project.computer) return <Laptop size={size} className={`shrink-0 ${className}`} aria-hidden />;
  return <StoredProjectIcon project={project} size={size} className={className} />;
}

function StoredProjectIcon({ project, size, className }: { project: { id: number; icon?: string }; size: number; className: string }) {
  const icon = project.icon ?? '';
  const editorEnabled = usePluginPresent('editor');
  const { data: src } = useQuery({
    queryKey: ['project-icon', project.id, icon],
    enabled: !!icon && editorEnabled,
    staleTime: Infinity,
    queryFn: async () => {
      const system = icon.startsWith('/');
      const blob = await elowenClient.projectRawBlob(project.id, system ? icon.slice(1) : icon, system ? 'system' : 'project');
      return await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result as string);
        fr.onerror = () => reject(fr.error);
        fr.readAsDataURL(blob);
      });
    },
  });
  if (icon && src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={src} alt="" aria-hidden data-project-icon={icon} className={`shrink-0 rounded-sm object-cover ${className}`} style={{ width: size, height: size }} />
    );
  }
  return <FolderGit2 size={size} data-project-icon={icon || undefined} className={`shrink-0 ${className}`} aria-hidden />;
}
